import { httpClient } from "@/lib/http";
import { AUDIT_ENDPOINTS } from "@/config/api";
import type { ApiResponse } from "@/types/api";
import type {
    AuditLogsQueryParams,
    AuditLogsListResponse,
    AuditLogDetails,
    AdminActionItem,
    UserActionItem,
    ResourceAuditItem,
    FailedActionsQueryParams,
    FailedActionsResponse,
    SensitiveActionsQueryParams,
    SensitiveActionsResponse,
    AuditStatsQueryParams,
    AuditStatisticsResponse,
    IpActivityQueryParams,
    IpActivityEntry,
    ExportQueryParams,
    ExportAuditLogsResponse,
    MyActionItem,
} from "../types";

// ============================================================================
// 15.1 – Get Audit Logs
// ============================================================================

export const getAuditLogs = async (
    params?: AuditLogsQueryParams
): Promise<ApiResponse<AuditLogsListResponse>> => {
    const { data } = await httpClient.get<ApiResponse<AuditLogsListResponse>>(
        AUDIT_ENDPOINTS.LOGS,
        { params }
    );
    return data;
};

// ============================================================================
// 15.2 – Get Audit Log Details
// ============================================================================

export const getAuditLogDetails = async (
    id: string
): Promise<ApiResponse<AuditLogDetails>> => {
    const { data } = await httpClient.get<ApiResponse<AuditLogDetails>>(
        AUDIT_ENDPOINTS.LOG_DETAILS(id)
    );
    return data;
};

// ============================================================================
// 15.3 – Get Admin's Recent Actions
// ============================================================================

export const getAdminActions = async (
    adminId: string
): Promise<ApiResponse<AdminActionItem[]>> => {
    const { data } = await httpClient.get<ApiResponse<AdminActionItem[]>>(
        AUDIT_ENDPOINTS.ADMIN_ACTIONS(adminId)
    );
    return data;
};

// ============================================================================
// 15.4 – Get Actions On User
// ============================================================================

export const getUserActions = async (
    userId: string
): Promise<ApiResponse<UserActionItem[]>> => {
    const { data } = await httpClient.get<ApiResponse<UserActionItem[]>>(
        AUDIT_ENDPOINTS.USER_ACTIONS(userId)
    );
    return data;
};

// ============================================================================
// 15.5 – Get Resource Audit Trail
// ============================================================================

export const getResourceAuditTrail = async (
    resourceType: string,
    resourceId: string
): Promise<ApiResponse<ResourceAuditItem[]>> => {
    const { data } = await httpClient.get<ApiResponse<ResourceAuditItem[]>>(
        AUDIT_ENDPOINTS.RESOURCE_TRAIL(resourceType, resourceId)
    );
    return data;
};

// ============================================================================
// 15.6 – Get Failed Actions
// ============================================================================

export const getFailedActions = async (
    params?: FailedActionsQueryParams
): Promise<ApiResponse<FailedActionsResponse>> => {
    const { data } = await httpClient.get<ApiResponse<FailedActionsResponse>>(
        AUDIT_ENDPOINTS.FAILED_ACTIONS,
        { params }
    );
    return data;
};

// ============================================================================
// 15.7 – Get Sensitive Actions
// ============================================================================

export const getSensitiveActions = async (
    params?: SensitiveActionsQueryParams
): Promise<ApiResponse<SensitiveActionsResponse>> => {
    const { data } = await httpClient.get<ApiResponse<SensitiveActionsResponse>>(
        AUDIT_ENDPOINTS.SENSITIVE_ACTIONS,
        { params }
    );
    return data;
};

// ============================================================================
// 15.8 – Get Audit Statistics
// ============================================================================

export const getAuditStatistics = async (
    params?: AuditStatsQueryParams
): Promise<ApiResponse<AuditStatisticsResponse>> => {
    const { data } = await httpClient.get<ApiResponse<AuditStatisticsResponse>>(
        AUDIT_ENDPOINTS.STATISTICS,
        { params }
    );
    return data;
};

// ============================================================================
// 15.9 – Get Admin IP Activity
// ============================================================================

export const getAdminIpActivity = async (
    adminId: string,
    params?: IpActivityQueryParams
): Promise<ApiResponse<IpActivityEntry[]>> => {
    const { data } = await httpClient.get<ApiResponse<IpActivityEntry[]>>(
        AUDIT_ENDPOINTS.IP_ACTIVITY(adminId),
        { params }
    );
    return data;
};

// ============================================================================
// 15.10 – Get Action Categories
// ============================================================================

export const getActionCategories = async (): Promise<ApiResponse<string[]>> => {
    const { data } = await httpClient.get<ApiResponse<string[]>>(AUDIT_ENDPOINTS.CATEGORIES);
    return data;
};

// ============================================================================
// 15.11 – Get Audit Statuses
// ============================================================================

export const getAuditStatuses = async (): Promise<ApiResponse<string[]>> => {
    const { data } = await httpClient.get<ApiResponse<string[]>>(AUDIT_ENDPOINTS.STATUSES);
    return data;
};

// ============================================================================
// 15.12 – Export Audit Logs
// ============================================================================

export const exportAuditLogs = async (
    params: ExportQueryParams
): Promise<ApiResponse<ExportAuditLogsResponse>> => {
    const { data } = await httpClient.get<ApiResponse<ExportAuditLogsResponse>>(
        AUDIT_ENDPOINTS.EXPORT,
        { params }
    );
    return data;
};

// ============================================================================
// 15.13 – Get My Actions
// ============================================================================

export const getMyActions = async (): Promise<ApiResponse<MyActionItem[]>> => {
    const { data } = await httpClient.get<ApiResponse<MyActionItem[]>>(
        AUDIT_ENDPOINTS.MY_ACTIONS
    );
    return data;
};
